import React, { useEffect, useState } from 'react';
import { AdminLayout } from './AdminDashboard';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Mail, Phone, CheckCircle, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/contact/messages`, {
        headers: getAuthHeader()
      });
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (messageId, status) => {
    try {
      await axios.put(
        `${API_URL}/admin/contact/messages/${messageId}/status`,
        null,
        {
          headers: getAuthHeader(),
          params: { status }
        }
      );
      toast.success('Status updated');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  if (loading) return <AdminLayout title="Messages"><div>Loading...</div></AdminLayout>;

  return (
    <AdminLayout title="Contact Messages">
      <div className="space-y-6">
        {messages.length === 0 ? (
          <Card className="border-0 shadow-lg">
            <CardContent className="p-12 text-center">
              <p className="text-gray-600">No messages yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6">
            {messages.map((message) => (
              <Card key={message.id} className={`border-2 ${
                message.status === 'new' ? 'border-sage-400' : 'border-gray-200'
              } shadow-lg`}>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{message.name}</h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                          <span className="flex items-center">
                            <Mail className="h-4 w-4 mr-1" />
                            {message.email}
                          </span>
                          {message.phone && (
                            <span className="flex items-center">
                              <Phone className="h-4 w-4 mr-1" />
                              {message.phone}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          message.status === 'new' 
                            ? 'bg-amber-100 text-amber-800'
                            : message.status === 'read'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {message.status}
                        </span>
                        <span className="text-xs text-gray-500">
                          {new Date(message.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                    </div>

                    <div className="flex space-x-2">
                      {message.status === 'new' && (
                        <Button
                          onClick={() => updateStatus(message.id, 'read')}
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Eye className="mr-2 h-4 w-4" />
                          Mark as Read
                        </Button>
                      )}
                      {message.status !== 'replied' && (
                        <Button
                          onClick={() => updateStatus(message.id, 'replied')}
                          size="sm"
                          className="bg-green-600 hover:bg-green-700 text-white"
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Mark as Replied
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminMessages;
